// Satellite API wrapper.

import type {
  AnomalyOverviewResponse,
  DeltaBundle,
  SatelliteAlert,
  SatelliteIndex,
  SatelliteObservation,
  SatelliteRefreshResult,
  SatelliteStatus,
} from '@greeniq/shared';
import { api } from './client.js';

export interface ObservationsResponse {
  observations: SatelliteObservation[];
}

export interface OverviewResponse {
  index: SatelliteIndex;
  /** Sorted ASC. Every date where at least one zone has an observation. */
  dates: string[];
  /** zoneId → date → { value (median of chosen index), isLowConfidence }. */
  byZone: Record<number, Record<string, { value: number | null; isLowConfidence: boolean }>>;
}

export function getSatelliteStatus(courseSlug: string): Promise<SatelliteStatus> {
  return api.get<SatelliteStatus>(`/courses/${courseSlug}/satellite/status`);
}

export function getObservations(
  courseSlug: string,
  zoneId: number,
  opts: { from?: string; to?: string } = {},
): Promise<ObservationsResponse> {
  const params = new URLSearchParams();
  if (opts.from) params.set('from', opts.from);
  if (opts.to) params.set('to', opts.to);
  const qs = params.toString();
  return api.get<ObservationsResponse>(
    `/courses/${courseSlug}/zones/${zoneId}/observations${qs ? `?${qs}` : ''}`,
  );
}

export function refreshZone(
  courseSlug: string,
  zoneId: number,
): Promise<SatelliteRefreshResult> {
  return api.post<SatelliteRefreshResult>(
    `/courses/${courseSlug}/zones/${zoneId}/satellite/refresh`,
  );
}

export function getCourseOverview(
  courseSlug: string,
  index: SatelliteIndex,
  opts: { from?: string; to?: string } = {},
): Promise<OverviewResponse> {
  const params = new URLSearchParams({ index });
  if (opts.from) params.set('from', opts.from);
  if (opts.to) params.set('to', opts.to);
  return api.get<OverviewResponse>(
    `/courses/${courseSlug}/satellite/overview?${params.toString()}`,
  );
}

export function getAnomalyOverview(
  courseSlug: string,
  index: SatelliteIndex,
  atDate?: string,
): Promise<AnomalyOverviewResponse> {
  const params = new URLSearchParams({ index });
  if (atDate) params.set('at', atDate);
  return api.get<AnomalyOverviewResponse>(
    `/courses/${courseSlug}/satellite/anomaly-overview?${params.toString()}`,
  );
}

export interface DeltasResponse {
  atDate: string | null;
  deltas: DeltaBundle | null;
}

export function getDeltas(
  courseSlug: string,
  zoneId: number,
  atDate?: string,
): Promise<DeltasResponse> {
  const qs = atDate ? `?at=${encodeURIComponent(atDate)}` : '';
  return api.get<DeltasResponse>(
    `/courses/${courseSlug}/zones/${zoneId}/satellite/deltas${qs}`,
  );
}

export interface AlertsResponse {
  alerts: SatelliteAlert[];
}

export function getAlerts(
  courseSlug: string,
  status: 'unacknowledged' | 'acknowledged' | 'dismissed' | 'all' = 'unacknowledged',
  limit = 50,
): Promise<AlertsResponse> {
  return api.get<AlertsResponse>(
    `/courses/${courseSlug}/satellite/alerts?status=${status}&limit=${limit}`,
  );
}

export function acknowledgeAlert(
  courseSlug: string,
  alertId: number,
): Promise<{ alert: SatelliteAlert }> {
  return api.post<{ alert: SatelliteAlert }>(
    `/courses/${courseSlug}/satellite/alerts/${alertId}/acknowledge`,
  );
}

export interface TimelapseFrame {
  id: number;
  frameDate: string; // YYYY-MM-DD
  observationDate: string | null;
  cloudCover: number | null;
  width: number;
  height: number;
  bbox: [number, number, number, number]; // [west, south, east, north]
  url: string; // absolute or same-origin path
}

export function getTimelapseFrames(courseSlug: string): Promise<{ frames: TimelapseFrame[] }> {
  return api.get<{ frames: TimelapseFrame[] }>(`/courses/${courseSlug}/timelapse/frames`);
}
